import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function InsightShareModal({ isOpen, onClose }) {
    const [shareUrl, setShareUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const creationInitiated = useRef(false);

    useEffect(() => {
        if (isOpen && !shareUrl && !creationInitiated.current) {
            creationInitiated.current = true;
            createShareUrl();
        }
    }, [isOpen, shareUrl]);

    const createShareUrl = async () => {
        if (isLoading) return;
        setIsLoading(true);
        setError('');
        try {
            const url = typeof window !== 'undefined' ? window.location.href : '';
            setShareUrl(url);
        } catch (err) {
            setError(err.message || 'Có lỗi xảy ra khi tạo liên kết chia sẻ');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = async () => {
        if (shareUrl) {
            await performCopy();
        }
    };

    const performCopy = async (urlToCopy = shareUrl) => {
        try {
            await navigator.clipboard.writeText(urlToCopy);
            const button = document.getElementById('insight-copy-button');
            if (button) {
                button.textContent = 'Đã copy!';
                setTimeout(() => {
                    button.textContent = 'Copy link';
                }, 2000);
            }
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = urlToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }
    };

    const resetModal = () => {
        setShareUrl('');
        setError('');
        creationInitiated.current = false;
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 sm:p-6 min-h-screen">
            <div className="bg-app-bg rounded-lg shadow-xl w-full max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Chia sẻ báo cáo
                        </h3>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Link chia sẻ:
                            </label>
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    value={shareUrl || (isLoading ? 'Đang tạo...' : '')}
                                    readOnly
                                    className="flex-1 px-3 py-2 border border-app-border rounded-md bg-app-sidebar text-sm"
                                    placeholder={isLoading ? 'Đang tạo link chia sẻ...' : 'Link sẽ xuất hiện ở đây'}
                                />
                            </div>
                        </div>

                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}

                        <div className="flex space-x-3 pt-4">
                            <button
                                id="insight-copy-button"
                                onClick={copyToClipboard}
                                disabled={isLoading || !shareUrl}
                                className="flex-1 px-4 py-2 bg-brand-blue-500 text-white rounded-md hover:bg-brand-blue-600 disabled:bg-brand-blue-400 disabled:cursor-not-allowed transition-colors"
                            >
                                {isLoading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Đang tạo...
                                    </div>
                                ) : (
                                    'Copy link'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
